
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Loader2 } from "lucide-react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { useSubjectSelector } from "@/common";
import { notificationStore } from "@/store/notification-store";
import { TemplateConfiguration, TemplateMetaTagConfiguration, MetaTag } from "@/models/interface";
import { useNotificationCenterApi } from "@/hooks";
import useTranslation from "@/hooks/use-translation"; 
import { Editor } from "@tinymce/tinymce-react"
import { env } from "@/lib/env";
import { extractAndValidateMetaTags, getSampleValueByLanguage } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { EditMessageContext, TemplateSaveAction } from "@/models/enum";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { OverwriteConfirmationDialog } from "./overwrite-confirmation-dialog";
import { NewTemplateDialog } from "./new-template-dialog";

const EditorStyles = `
  html, body {
    width: 100% !important;
    max-width: 600px !important;
    box-sizing: border-box;
    overflow-x: hidden;
  }
  table, td, th {
    border: none !important;
    outline: none !important;
  }

  table {
    width: 100% !important;
    max-width: 100% !important;
    table-layout: fixed !important;
    border-collapse: collapse;
  }

  td, th {
    word-break: break-word;
  }

  img {
    max-width: 100% !important;
    height: auto;
  }
`
export const EditMessageModal: FC<{
    editMessageOpen: boolean,
    onOpenChange: (open: boolean) => void
    isSavingTemplate: boolean
    editingTemplate: TemplateConfiguration | null
    setEditingTemplate: (template: TemplateConfiguration | null) => void
    editMessageContext: EditMessageContext,
    setEditMessageOpen: (open: boolean) => void
    setIsSavingTemplate: React.Dispatch<React.SetStateAction<boolean>>
}> = ({editMessageOpen, 
    onOpenChange, 
    isSavingTemplate, 
    editingTemplate, 
    setEditingTemplate,
    editMessageContext,
    setEditMessageOpen,
    setIsSavingTemplate
}) => {
    const { t, language } = useTranslation()
    const { toast } = useToast()
    const editorRef = useRef<any>(null);
    const { templates, templateMetaTags, selectedRecipientType } = useSubjectSelector(notificationStore, ['templates', 'templateMetaTags', 'selectedRecipientType'])
    const [filteredMetaTags, setFilteredMetaTags] = useState<MetaTag[]>([])
    const [selectedMetaTagConfiguration, setSelectedMetaTagConfiguration] = useState<TemplateMetaTagConfiguration | null>(null)
    const [showOverwriteDialog, setShowOverwriteDialog] = useState(false)
    const [showNewTemplateDialog, setShowNewTemplateDialog] = useState(false)

    const { fetchTemplateMetaTagConfigurationById } = useNotificationCenterApi()
    
    const saveOptions = [
      { key: TemplateSaveAction.SaveChanges, label: t("dashboard.dialogs.saveChanges") },
      { key: TemplateSaveAction.SaveAsNew, label: t("dashboard.dialogs.saveAsNew") },
    ] as const
    
    // Language tab state
    const [activeLanguage, setActiveLanguage] = useState<"en" | "fr">("en")
    const [contentByLanguage, setContentByLanguage] = useState<{
      en: { subject: string; content: string }
      fr: { subject: string; content: string }
    }>({
      en: { subject: "", content: "" },
      fr: { subject: "", content: "" }
    })

    // Language index mapping for EmailTemplate array
    const LANGUAGE_INDEX_MAP = {
      "en": 0,
      "fr": 1
    } as const

    // Helper functions to work with EmailTemplate array
    const getTemplateByLanguage = (template: TemplateConfiguration, language: "en" | "fr") => {
      const index = LANGUAGE_INDEX_MAP[language]
      return template.Data?.EmailTemplates?.[index]
    }

    const updateTemplateByLanguage = (template: TemplateConfiguration, language: "en" | "fr", updates: { Subject?: string; Body?: string }) => {
      const index = LANGUAGE_INDEX_MAP[language]
      const updatedEmailTemplates = [...template.Data?.EmailTemplates]
      updatedEmailTemplates[index] = {
        ...updatedEmailTemplates[index],
        ...updates
      }
      
      const result: TemplateConfiguration = {
        ...template,
        Data: {
          ...template.Data,
          EmailTemplates: updatedEmailTemplates
        }
      }
      return result
    }

    const { updateTemplateConfiguration, createTemplateConfiguration } = useNotificationCenterApi()

    const handleEditorChange = useCallback((content: string) => {
      setContentByLanguage(prev => ({
        ...prev,
        [activeLanguage]: {
          ...prev[activeLanguage],
          content: content
        }
      }));
      
      // Update the template object
      if (editingTemplate) {
        const updatedTemplate = updateTemplateByLanguage(editingTemplate, activeLanguage, { Body: content })
        setEditingTemplate(updatedTemplate);
      }
    }, [activeLanguage, editingTemplate])

    const handleSave = useCallback(async (action: TemplateSaveAction, newTemplateName?: string) => {
      if (!editingTemplate) {
        return
      }

      setIsSavingTemplate(true)
      
      try {
        // Update both language versions with current state
        let updatedTemplate: TemplateConfiguration = editingTemplate
        
        // Update English template
        const enContent = contentByLanguage.en.content || getTemplateByLanguage(editingTemplate, "en").Body
        const enSubject = contentByLanguage.en.subject || getTemplateByLanguage(editingTemplate, "en").Subject
        updatedTemplate = updateTemplateByLanguage(updatedTemplate, "en", { 
          Subject: enSubject, 
          Body: enContent 
        })
        
        // Update French template
        const frContent = contentByLanguage.fr.content || getTemplateByLanguage(editingTemplate, "fr").Body
        const frSubject = contentByLanguage.fr.subject || getTemplateByLanguage(editingTemplate, "fr").Subject
        updatedTemplate = updateTemplateByLanguage(updatedTemplate, "fr", { 
          Subject: frSubject, 
          Body: frContent 
        })

        // Extract and validate meta tags from both language versions
        let allValidMetaTags: MetaTag[] = []
        let allInvalidTags: string[] = []
        
        if (filteredMetaTags.length > 0) {
          // Get meta tags from English content
          const enResult = extractAndValidateMetaTags(
            enSubject,
            enContent,
            filteredMetaTags,
            editingTemplate
          )
          
          // Get meta tags from French content
          const frResult = extractAndValidateMetaTags(
            frSubject,
            frContent,
            filteredMetaTags,
            editingTemplate
          )
          
          // Combine and deduplicate valid meta tags
          const uniqueValidTagsMap = new Map<string, MetaTag>()
          for (const tag of enResult.validMetaTags) {
            if (!uniqueValidTagsMap.has(tag.Key)) uniqueValidTagsMap.set(tag.Key, tag)
          }
          for (const tag of frResult.validMetaTags) {
            if (!uniqueValidTagsMap.has(tag.Key)) uniqueValidTagsMap.set(tag.Key, tag)
          }
          const uniqueValidTags = Array.from(uniqueValidTagsMap.values())

          // Combine and deduplicate invalid tags
          const invalidTagSet = new Set<string>()
          for (const tag of enResult.invalidTags) invalidTagSet.add(tag)
          for (const tag of frResult.invalidTags) invalidTagSet.add(tag)
          const uniqueInvalidTags = Array.from(invalidTagSet)
          
          allValidMetaTags = uniqueValidTags
          allInvalidTags = uniqueInvalidTags
        }

        // Show error toast if invalid tags are found
        if (allInvalidTags.length > 0) {
          toast({
            title: t("dashboard.editMessage.invalidTagsTitle"),
            description: t("dashboard.editMessage.invalidTagsDescription", { 
              tags: allInvalidTags.map(tag => `{{${tag}}}`).join(", ")
            }),
            variant: "destructive",
          })
        }

        // Update template with validated meta tags
        updatedTemplate = {
          ...updatedTemplate,
          Data: {
            ...updatedTemplate.Data,
            MetaTag: {
              TemplateMetaTagId: selectedMetaTagConfiguration?.id || "",
              SelectedMetaTags: allValidMetaTags
            }
          }
        }

        // Determine if we need to create a new customized template or update existing one
        const shouldCreateNewTemplate = action === TemplateSaveAction.SaveAsNew

        if (shouldCreateNewTemplate) {
          // Create new customized template
          const finalTemplate: TemplateConfiguration = {
            ...updatedTemplate,
            id: undefined,
            Data: {
              ...updatedTemplate.Data,
              IsCustomizedForReminder: true,
              CustomizedTemplateSourceId: editingTemplate.id ?? "",
              TemplateName: newTemplateName || '',
            }
          }
          
          // Create new template via API
          const createdTemplate = await createTemplateConfiguration(finalTemplate)
          notificationStore.dispatch({
            templates: [...templates, createdTemplate],
            newlyCreatedTemplateId: createdTemplate.id ?? null
          })

          toast({
            title: t("dashboard.dialogs.templateSavedSuccessfully"),
            variant: "success",
          })
        }
        else{
          // Update existing template in place
          const finalTemplate: TemplateConfiguration = updatedTemplate

          await updateTemplateConfiguration(finalTemplate)
          
          // Update local state in notification store
          const updatedTemplates = templates.map((t) => 
            t.id === editingTemplate.id ? finalTemplate : t
          )
          notificationStore.setTemplates(updatedTemplates)
        }
        
        // Close the modal
        setEditMessageOpen(false)
        setEditingTemplate(null)
      } catch (error) {
        toast({
          description: 'Error saving template',
          variant: "destructive"
        })
      } finally {
        setIsSavingTemplate(false)
      }
    }, [editingTemplate, templates, toast, t])

    // Filter MetaTags
    useEffect(() => {
      if(editingTemplate?.Data?.MetaTag?.TemplateMetaTagId){
        fetchTemplateMetaTagConfigurationById(editingTemplate?.Data?.MetaTag?.TemplateMetaTagId).then((response) => {
          setSelectedMetaTagConfiguration(response)
          setFilteredMetaTags(response?.Data?.AllowedMetaTags || [])
        })
      }
    }, [templateMetaTags, editingTemplate?.Data?.EntityTypes, selectedRecipientType])
    
    // Initialize content when modal opens
    useEffect(() => {
      if (editMessageOpen && editingTemplate) {
        const enTemplate = getTemplateByLanguage(editingTemplate, "en")
        const frTemplate = getTemplateByLanguage(editingTemplate, "fr")
        
        setContentByLanguage({
          en: { 
            subject: enTemplate.Subject, 
            content: enTemplate.Body 
          },
          fr: { 
            subject: frTemplate.Subject, 
            content: frTemplate.Body 
          }
        });
      }
    }, [editMessageOpen, editingTemplate]);

    // Update editor content when switching languages
    useEffect(() => {
      if (editorRef.current && editMessageOpen) {
        const currentContent = contentByLanguage[activeLanguage].content;
        if (editorRef.current.getContent() !== currentContent) {
          editorRef.current.setContent(currentContent);
        }
      }
    }, [activeLanguage, contentByLanguage, editMessageOpen]);

    return (
        <Dialog open={editMessageOpen} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t("dashboard.editMessage.title")}</DialogTitle>
                <DialogDescription>
                  {t("dashboard.editMessage.description")}{editMessageContext === "create" ? t("dashboard.editMessage.forNewReminder") : ""}.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6">
                <div className="flex flex-row ">
                  <div className="w-3/4 space-y-4 p-4 border rounded-lg bg-gray-50">
                    {/* Language Tabs */}
                    <div className="flex space-x-1 bg-gray-200 p-1 rounded-lg w-fit">
                      <button
                        onClick={() => setActiveLanguage("en")}
                        className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                          activeLanguage === "en"
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        English
                      </button>
                      <button
                        onClick={() => setActiveLanguage("fr")}
                        className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                          activeLanguage === "fr"
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        Français
                      </button>
                    </div>
                    
                    <div>
                      <Label htmlFor="edit-message-subject">
                        {t("dashboard.editMessage.emailSubject")}
                      </Label>
                      <Input
                          id="edit-message-subject"
                          value={contentByLanguage[activeLanguage].subject}
                          onChange={(e) => {
                            const newSubject = e.target.value;
                            setContentByLanguage(prev => ({
                              ...prev,
                              [activeLanguage]: {
                                ...prev[activeLanguage],
                                subject: newSubject
                              }
                            }));
                            
                            // Update the template object
                            if (editingTemplate) {
                              const updatedTemplate = updateTemplateByLanguage(editingTemplate, activeLanguage, { Subject: newSubject })
                              setEditingTemplate(updatedTemplate);
                            }
                          }}
                          placeholder={t("dashboard.editMessage.emailSubjectPlaceholder")}
                      />
                    </div>

                    <div>
                      <Label htmlFor="edit-message-content">
                        {t("dashboard.editMessage.emailContent")}
                      </Label>
                      <div className="border rounded-md max-w-md">
                        <Editor 
                          value={contentByLanguage[activeLanguage].content}
                          onEditorChange={handleEditorChange}
                          apiKey={env.tinyMceApiKey} 
                          init={{
                            resize: false,
                            max_height: 500,
                            width: '100%',
                            menubar: false,
                            branding: false,
                            toolbar: false,
                            elementpath: false,
                            content_style: EditorStyles,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="w-2/4 mx-4">
                    <span className="text-lg font-bold">{t("dashboard.editMessage.availableTags")}</span>

                    {filteredMetaTags.length > 0 ? (
                      <ul className="list-disc list-inside mt-4 p-4 bg-gray-100 rounded-lg">
                        {filteredMetaTags.map((metaTag, index) => {
                          const sampleValue = getSampleValueByLanguage(metaTag.SampleValues, language, false)
                          return (
                          <li key={metaTag.Key} className={`text-sm ${index > 0 ? 'mt-2' : ''}`}>
                            <span className="font-bold">
                              {metaTag.Key}
                            </span>
                            <span>{sampleValue ? ` - ${sampleValue}` : ''}</span>
                            
                            {/* Show SubMetaTags if they exist and are not empty */}
                            {metaTag?.SubMetaTags && metaTag?.SubMetaTags?.length > 0 && (
                              <ul className="list-disc list-inside ml-6 mt-2">
                                {metaTag.SubMetaTags.map((subMetaTag, subIndex) => (
                                  <li key={subMetaTag.Key} className={`text-sm text-gray-600 ${subIndex > 0 ? 'mt-1' : ''}`}>
                                    <span className="font-medium">
                                      {subMetaTag.Value}
                                    </span> - <span>{getSampleValueByLanguage(subMetaTag.SampleValues, language, false)}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        )})}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-600">{t("dashboard.editMessage.noTagsFound")}</p>
                    )}
                  </div>
                </div> 

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    {t("dashboard.editMessage.cancel")}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        className="bg-slate-800 hover:bg-slate-700"
                        disabled={isSavingTemplate || !contentByLanguage[activeLanguage].subject.trim() || !contentByLanguage[activeLanguage].content?.trim()}
                      >
                        {isSavingTemplate ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t("dashboard.editMessage.saving")}
                          </>
                        ) : (
                          t("dashboard.editMessage.saveChanges")
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      {saveOptions.map(option => (
                        <DropdownMenuItem
                          key={option.key}
                          onSelect={() => {
                            if (option.key === TemplateSaveAction.SaveChanges) {
                              setShowOverwriteDialog(true)
                              return
                            }
                            setShowNewTemplateDialog(true)
                          }}
                        >
                          {option.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <NewTemplateDialog
                open={showNewTemplateDialog}
                initialName={editingTemplate?.Data?.TemplateName || ''}
                onCancel={() => setShowNewTemplateDialog(false)}
                onConfirm={async (name) => {
                  setShowNewTemplateDialog(false)
                  await handleSave(TemplateSaveAction.SaveAsNew, name)
                }}
              />
              <OverwriteConfirmationDialog
                open={showOverwriteDialog}
                templateName={editingTemplate?.Data?.TemplateName || ""}
                onCancel={() => setShowOverwriteDialog(false)}
                onConfirm={async () => {
                  setShowOverwriteDialog(false)
                  await handleSave(TemplateSaveAction.SaveChanges)
                }}
              />
            </DialogContent>
        </Dialog>
    )
}